// The draft recap (DRAFT_RECAP.md, scenario C6): my grade first, every
// team ranked, the steals and the reaches, each a line worth arguing over,
// and a share card for the group chat.

import { useState } from 'react';
import { Button, Cover, Text, Title } from '../kit/ui';
import '../styles/screens/waivers.css';
import '../styles/screens/league-screens.css';
import type { DraftPickNote, DraftRecapView, DraftTeamGrade } from '@fsp/domain';

import { useDraftRecap, useLeagueBase } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { Cap, positionLabel, PositionPill, Surface, TeamMark } from '../kit';
import { useTier } from '../shell/useTier';
import { DashboardScreen } from '../templates/DashboardScreen';
import { DetailSheet } from '../templates/DetailSheet';
import { SharePanel } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { PlayerName, TeamName } from './shared/Names';
import { Term } from '../kit/Why';
import { useTried } from '../data/tried';

/**
 * Who decided a pick, in a word.
 *
 * Plain nouns rather than the stored ids: `rankings` is the house board and
 * nobody outside this codebase calls it that. Nothing here grades the choice —
 * "tools, not instructions" — it just says whose choice it was.
 */
const BY_LABEL: Record<'user' | 'queue' | 'plan' | 'rankings' | 'ai' | 'keeper', string> = {
  user: 'picked',
  queue: 'from the queue',
  plan: 'from their plan',
  rankings: 'from the board',
  ai: 'a bot',
  // Last season's player, kept on this pick (H6, Q7).
  keeper: 'kept',
};

/**
 * ON THE API SINCE D6. The grades come from stored picks for a real league and
 * from the simulator for the practice one, and this screen cannot tell which
 * — which is the point of `Snapshot`: it draws the three answers a real league
 * has (not yet, here it is, no) rather than assuming a value is always there.
 */

const STEAL =
  'A steal: a player taken well after where the board this league drafted from expected him to go.';
const REACH =
  'A reach: a player taken well before where the board this league drafted from expected him to go.';

export function DraftRecapScreen() {
  const snapshot = useDraftRecap();
  return <FromLeague snapshot={snapshot}>{(view) => <Recap view={view} />}</FromLeague>;
}

function Recap({ view }: { view: DraftRecapView }) {
  useTried('draft-recap');
  const tier = useTier();
  const phone = tier === 'phone';
  const base = useLeagueBase();
  const real = useLeagueSource() === 'api';
  const [share, setShare] = useState(false);
  // WHAT DID THEY ACTUALLY DRAFT, which is the question a grade provokes and
  // the only thing worth doing on this page. It opened the Awards screen's
  // room tab until 2026-09-22, and for a real league that is a screen still
  // reading the simulator — so the one interaction on the recap landed on
  // "there is nothing truthful to show you here yet", and the page offered
  // nothing beyond its top-level summary. The picks ride on the view now, so
  // the answer is a sheet rather than a navigation.
  const [openTeam, setOpenTeam] = useState<string | null>(null);
  const shown = view.teams.find((t) => t.team.id === openTeam) ?? null;

  if (!view.available)
    return (
      <div className="fsp-screen">
        <div className="fsp-empty">
          {view.league.status === 'setup'
            ? 'The recap is written the moment the draft ends.'
            : 'This league did not draft here, so there is nothing to grade.'}
        </div>
      </div>
    );

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>Draft recap</Title>
        <div className="fsp-sub fsp-ui-wrap-text">{view.headline}</div>
      </div>
      <div className="fsp-drecap-actions">
        {/* THE DRAFT PLAYED BACK (C11): a real league's, which keeps its board. */}
        {real ? (
          <Button size="xs" icon="play" to={`${base}/draft/replay`}>
            Replay
          </Button>
        ) : null}
        {view.mine ? (
          <Button size="xs" icon="share" onClick={() => setShare(true)}>
            Share
          </Button>
        ) : null}
      </div>
    </div>
  );

  const gradeCard = (t: DraftTeamGrade, big: boolean) => (
    <div
      key={t.team.id}
      className={`fsp-surface fsp-grade ${big ? 'big' : ''} fsp-row-clickable fsp-cover-host`}
    >
      {/* The card opens the team's picks; its name opens the team, above
          that door (B-220). */}
      <Cover label={`${t.team.name}: every pick`} onClick={() => setOpenTeam(t.team.id)} />
      <span className="fsp-grade-letter fsp-disp" data-grade={t.grade}>
        {t.grade}
      </span>
      <div className="fsp-waivers-grow">
        <div className="fsp-name">
          <span className="fsp-num fsp-muted">{t.rank}</span>
          <TeamMark team={t.team} size={big ? 26 : 20} />
          <span className="fsp-name-text">
            <TeamName team={t.team} />
          </span>
        </div>
        <div className="fsp-sub fsp-ui-wrap-text">{t.line}</div>
        {/* A BOT'S STYLE IN ITS PICKS (AI_MANAGERS.md §4.7): "Grandma Dot
            took 4 Vikings, and no Packers." */}
        {t.style ? <div className="fsp-sub fsp-ui-wrap-text">{t.style}</div> : null}
        <div className="fsp-grade-facts">
          {/* What a steal and a reach are, on a tap or a hover: the grade
              rests on them and nothing said what they meant (the page-by-page
              review of 2026-10-11). */}
          <Term tip={STEAL}>{`${t.steals} steal${t.steals === 1 ? '' : 's'}`}</Term>
          <Term tip={REACH}>{`${t.reaches} reach${t.reaches === 1 ? '' : 'es'}`}</Term>
          <span>
            {t.positions.map((p) => `${p.count} ${positionLabel(p.position)}`).join(' · ')}
          </span>
        </div>
      </div>
    </div>
  );

  // AN AUCTION'S RECAP says bargains and splurges: dollars, not places (AUCTION.md §7).
  const auction = view.teams.some((t) => t.picks.some((p) => p.price !== null));
  const pickRow = (n: DraftPickNote) => (
    <div key={`${n.team.id}:${n.player.id}`} className="fsp-row fsp-drecap-pick">
      <PositionPill pos={n.player.positions[0]!} size="sm" />
      <div className="fsp-waivers-who">
        <div className="fsp-name">
          <PlayerName player={n.player} className="fsp-name-text" club={n.player.proTeam} />
          <span className="fsp-sub">
            {/* "board", not "ADP". There is no average draft position in this
                product — nobody has run enough drafts for one to mean anything
                — and the number is this league's own board position, which the
                view says in `basis`. A label that claims ADP is a label that
                claims a fact we do not have. */}
            {n.price !== null
              ? `$${String(n.price)} · value $${String(n.adp)}`
              : `pick ${String(n.overall)} · board ${String(n.adp)}`}
          </span>
        </div>
        <div className="fsp-sub fsp-ui-wrap-text">{n.line}</div>
      </div>
      {/* An auction's is dollars under value: "+$12", "−$8". */}
      <span className={`fsp-num fsp-drecap-delta ${n.delta >= 0 ? 'up' : 'down'}`}>
        {n.delta >= 0 ? '+' : '−'}
        {n.price !== null ? '$' : ''}
        {Math.abs(n.delta)}
      </span>
    </div>
  );

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">{header}</div>
      <DashboardScreen
        widgets={[
          ...(view.mine
            ? [{ id: 'mine', column: 1 as const, content: <>{gradeCard(view.mine, true)}</> }]
            : []),
          {
            id: 'grades',
            column: 1 as const,
            content: (
              <>
                <Cap>Every team, graded</Cap>
                <div className="fsp-grades">{view.teams.map((t) => gradeCard(t, false))}</div>
              </>
            ),
          },
          {
            id: 'steals',
            column: 2 as const,
            content: (
              <>
                <Cap>{auction ? 'Bargains' : 'Steals'}</Cap>
                <Surface>
                  {view.steals.length ? (
                    view.steals.map(pickRow)
                  ) : (
                    <div className="fsp-awards-note">
                      <Text tone="secondary">Nobody got a bargain. A very disciplined room.</Text>
                    </div>
                  )}
                </Surface>
              </>
            ),
          },
          {
            id: 'reaches',
            column: phone ? (2 as const) : (3 as const),
            content: (
              <>
                <Cap>{auction ? 'Splurges' : 'Reaches'}</Cap>
                <Surface>
                  {view.reaches.length ? (
                    view.reaches.map(pickRow)
                  ) : (
                    <div className="fsp-awards-note">
                      <Text tone="secondary">Nobody panicked. Suspicious.</Text>
                    </div>
                  )}
                </Surface>
              </>
            ),
          },
        ]}
      />
      <DetailSheet
        opened={shown !== null}
        onClose={() => setOpenTeam(null)}
        title={shown ? `${shown.team.name} · ${shown.grade}` : ''}
      >
        {shown ? (
          <div className="fsp-drecap-team">
            <div className="fsp-sub fsp-ui-wrap-text">{shown.line}</div>
            {shown.style ? <div className="fsp-sub fsp-ui-wrap-text">{shown.style}</div> : null}
            <Cap>Every pick, in order</Cap>
            <Surface>
              {/* AT 390 THE NAME GETS THE REST OF THE ROW. Four fixed columns
                  left about 160px for it and drew "Bijan Robi…", "Courtland …",
                  "Michael Pi…" — a list of picks you cannot read the picks off.
                  A name cut off at phone width is never acceptable, here or in
                  Standings, where the same defect was found. So on a phone
                  the source moves under the name instead of beside it; there
                  is room for both at tablet. */}
              {shown.picks.map((p) => (
                <div key={p.overall} className={`fsp-row fsp-drecap-row ${phone ? 'phone' : ''}`}>
                  {/* Round and pick, the way a draft board is read aloud; in an
                      auction, what he cost. */}
                  <span className="fsp-num fsp-muted">
                    {p.price !== null
                      ? `$${String(p.price)}`
                      : `${String(p.round)}.${String(((p.overall - 1) % view.teams.length) + 1).padStart(2, '0')}`}
                  </span>
                  <PositionPill pos={p.player.positions[0] ?? ''} size="sm" />
                  {/* WHOSE CHOICE IT WAS. `plan` is the manager's own pre-draft
                      list and it is the whole payoff of filling one in — a board
                      that called every autopick "rankings" would be telling
                      somebody their evening was ignored. */}
                  <div className="fsp-waivers-who">
                    <div className="fsp-name">
                      <PlayerName
                        player={p.player}
                        className="fsp-name-text"
                        club={p.player.proTeam}
                      />
                    </div>
                    {phone && p.by !== null ? (
                      <div className="fsp-sub">{BY_LABEL[p.by]}</div>
                    ) : null}
                  </div>
                  {phone ? null : (
                    <span className="fsp-sub">{p.by === null ? '' : BY_LABEL[p.by]}</span>
                  )}
                </div>
              ))}
            </Surface>
          </div>
        ) : null}
      </DetailSheet>
      {view.mine ? (
        <DetailSheet opened={share} onClose={() => setShare(false)} title="Share your grade">
          <SharePanel
            input={{
              kicker: 'Draft grade',
              headline: view.mine.grade,
              subline: `${view.mine.team.name} · ${view.mine.team.ownerName}`,
              line: view.mine.line,
              footer: `${view.league.name} · ${view.league.season} draft`,
              team: view.mine.team,
              tone:
                view.mine.rank <= 3
                  ? 'glory'
                  : view.mine.rank >= view.teams.length - 1
                    ? 'shame'
                    : 'fun',
            }}
            text={`${view.mine.team.name} got a ${view.mine.grade} in the ${view.league.name} draft. ${view.mine.line}`}
          />
        </DetailSheet>
      ) : null}
    </div>
  );
}
